import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { api } from '../api';
import { useSession } from './session-context';
import { useResource } from './use-resource';
import type { PlanEntitlements, Subscription } from '../mock/seeds/schema-types';

// The merchant's plan (GET /merchant/subscription, API-127; one per account, DR-78) for the shell: the
// rail/More-sheet lock glyphs and the route-level plan gate (FR-B22) read the same
// entitlements (DB-53a), so a locked item and its locked page cannot disagree.
// Entitlements are plan DATA — nothing here names a tier. Re-read on business switch
// (session epoch). A 404 (no subscription row) leaves entitlements unknown, which
// never locks anything. A team member's session (API-16) has no plan UI and the
// subscription read is merchant-only, so staff never make it (entitlements unknown).
interface PlanContextValue {
  subscription: Subscription | undefined;
  entitlements: PlanEntitlements | undefined;
  loading: boolean;
}

const PlanContext = createContext<PlanContextValue>({
  subscription: undefined,
  entitlements: undefined,
  loading: false,
});

export function PlanProvider({ children }: { children: ReactNode }) {
  const { user, epoch, isStaff } = useSession();
  const { data, loading } = useResource<Subscription | undefined>(
    async (signal) => {
      // DR-78: the plan is the merchant's (one row per account, a Free row before the
      // first listing) — read as soon as the session is known, business or not.
      if (!user || isStaff) return undefined;
      try {
        return await api.GET('/merchant/subscription', { signal });
      } catch {
        return undefined;
      }
    },
    [user?.id, epoch, isStaff],
  );
  const value = useMemo<PlanContextValue>(
    () => ({ subscription: data, entitlements: data?.plan?.entitlements, loading }),
    [data, loading],
  );
  return <PlanContext.Provider value={value}>{children}</PlanContext.Provider>;
}

export function usePlan(): PlanContextValue {
  return useContext(PlanContext);
}
